/**
 * Download meeting minutes as PDF, Word (.doc), or plain text — same formats as the hamburger menu.
 */

import { jsPDF } from 'jspdf';
import htmlDocx from 'html-docx-js/dist/html-docx';

/**
 * @param {string} minutesContent plain-text minutes body
 * @param {string} fileNameBase filename without extension
 * @param {'pdf'|'doc'|'txt'} format
 */
export function downloadMinutesExport(minutesContent, fileNameBase, format = 'txt') {
  const fileName = String(fileNameBase || 'Meeting_Minutes').replace(/[^\w\-]+/g, '_').slice(0, 120);

  if (format === 'pdf') {
    const pdf = new jsPDF();
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(minutesContent, 180);
    let yPosition = 20;
    const pageHeight = 280;
    const lineHeight = 5;
    lines.forEach((line) => {
      if (yPosition > pageHeight) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, 15, yPosition);
      yPosition += lineHeight;
    });
    pdf.save(`${fileName}.pdf`);
    return;
  }

  if (format === 'doc') {
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Meeting Minutes</title>
</head>
<body style="font-family: Times New Roman; font-size: 12pt; line-height: 1.5; margin: 1in;">
  ${minutesContent
    .split('\n')
    .map((line) => {
      if (line.trim() === '') return '<br/>';
      const escaped = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `<p style="margin: 0; padding: 0;">${escaped}</p>`;
    })
    .join('')}
</body>
</html>`;

    try {
      const converted = htmlDocx.asBlob(htmlContent);
      const url = window.URL.createObjectURL(converted);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('DOC generation failed:', error);
      const rtfContent = `{\\rtf1\\ansi\\deff0 {\\fonttbl {\\f0 Times New Roman;}}\\f0\\fs24 ${minutesContent.replace(/\n/g, '\\par ')}}`;
      const blob = new Blob([rtfContent], { type: 'application/rtf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.rtf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
    return;
  }

  const blob = new Blob([minutesContent], { type: 'text/plain;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
